import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { TabsEnum, transformVehicleTripSegments } from "../components/monitorView/monitorViewUtils";
import { WorkShiftDto } from "../models/workShift/workShift";
import { getVehicleTripSegments } from "../services/vehicleService";
import { VehicleTripSegment } from "../models/vehicle/vehicleTripSegment";
import { VehicleListItemDto } from "../models/transport/vehicle";
import { VehiclePositionInfo } from "../models/vehicle/vehiclePosition";
import { VehicleData } from "../models/monitorView/monitorViewTypes";
import { positionStreetAddressReq, positionStreetAddressRes } from "../models/geocode/positionTypes";
import { getBatchOfGeocodes } from "../services/geocodingService";

interface MonitorViewState {
    groupMapPoints: boolean;
    activeTab: number;
    isWorkShiftView: boolean;
    isVehicleRouteView: boolean;
    isVehicleDetailView: boolean;
    selectedVehicleId: string | null;
    selectedEmployeeId: string | null;
    selectedWorkShiftTimeSlotId: string | null;
    selectedWorkShift: WorkShiftDto | null;
    selectedRouteId: string | null;
    hoveredRouteId: string | null;
    sidebarTitle: string;
    vehicleRoute: VehicleTripSegment[] | null;
    mapData: VehicleData[];
    tripStartDate: Date | null;
    tripEndDate: Date | null;
    vehicles: VehicleListItemDto[] | null;
    positions: VehiclePositionInfo[] | null;
    loading: boolean;
    error: string | null;
    segmentStoppedAddressesLoading: boolean;
    vehicleAddressLoading: boolean;
    vehicleStreetAddress: string;
}

export const initialState: MonitorViewState = {
    groupMapPoints: true,
    activeTab: TabsEnum.VEHICLES,
    isWorkShiftView: false,
    isVehicleRouteView: false,
    isVehicleDetailView: false,
    selectedVehicleId: null,
    selectedEmployeeId: null,
    selectedWorkShiftTimeSlotId: null,
    selectedWorkShift: null,
    selectedRouteId: null,
    hoveredRouteId: null,
    sidebarTitle: "",
    vehicleRoute: null,
    mapData: null,
    tripStartDate: dayjs().startOf("day").toDate(),
    tripEndDate: dayjs().startOf("day").add(1, "day").toDate(),
    vehicles: null,
    positions: null,
    loading: false,
    error: null,
    segmentStoppedAddressesLoading: false,
    vehicleAddressLoading: false,
    vehicleStreetAddress: ""
};

export const fetchVehicleTripSegments = createAsyncThunk(
    "monitorView/fetchVehicleTripSegments",
    async({ vehicleId, startDate, endDate }: { vehicleId: string; startDate: string; endDate: string }, { rejectWithValue }) => {
        try {
            const response = await getVehicleTripSegments(vehicleId, startDate, endDate, false);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchGeocodedTripStopPositionAddresses = createAsyncThunk(
    "monitorView/fetchGeocodedStopPositionAddresses",
    async(request: positionStreetAddressReq[], { rejectWithValue }) => {
        try {
            const response = await getBatchOfGeocodes(request, true);
            return response.data;
        } catch (err) {
            return rejectWithValue("Failed to geocode addresses");
        }
    }
);

export const fetchGeocodedVehicleAddress = createAsyncThunk<
    positionStreetAddressRes | null,
    positionStreetAddressReq,
    { rejectValue: unknown }
>(
    "monitorView/fetchGeocodedVehicleAddress",
    async(request: positionStreetAddressReq, { rejectWithValue }) => {
        try {
            const response = await getBatchOfGeocodes([request], true);
            if (!response?.data || response.data.length === 0) {
                return null;
            }
            return response.data[0];
        } catch (err) {
            return rejectWithValue("Failed to geocode address for vehicle position");
        }
    }
);


export const monitorViewSlice = createSlice({
    name: "monitorView",
    initialState,
    reducers: {
        setGroupMapPoints: (state, action: PayloadAction<boolean>) => {
            state.groupMapPoints = action.payload;
        },
        setActiveTab: (state, action: PayloadAction<number>) => {
            state.activeTab = action.payload;
        },
        setIsWorkShiftView(state, action: PayloadAction<boolean>) {
            state.isWorkShiftView = action.payload;
        },
        toggleWorkShiftView(state) {
            state.isWorkShiftView = !state.isWorkShiftView;
        },
        setIsVehicleDetailView(state, action: PayloadAction<boolean>) {
            state.isVehicleDetailView = action.payload;
        },
        toggleVehicleDetailView(state) {
            state.isVehicleDetailView = !state.isVehicleDetailView;
        },
        setIsVehicleRouteView(state, action: PayloadAction<boolean>) {
            state.isVehicleRouteView = action.payload;
        },
        setSelectedVehicleId(state, action: PayloadAction<string | null>) {
            state.selectedVehicleId = action.payload;
        },
        setSelectedEmployeeId(state, action: PayloadAction<string | null>) {
            state.selectedEmployeeId = action.payload;
        },
        setSelectedWorkShiftTimeSlotId(state, action: PayloadAction<string | null>) {
            state.selectedWorkShiftTimeSlotId = action.payload;
        },
        setSelectedWorkShift: (state, action: PayloadAction<WorkShiftDto | null>) => {
            state.selectedWorkShift = action.payload;
        },
        setSelectedRouteId(state, action: PayloadAction<string | null>) {
            state.selectedRouteId = action.payload;
        },
        setHoveredRouteId(state, action: PayloadAction<string | null>) {
            state.hoveredRouteId = action.payload;
        },
        setSidebarTitle: (state, action: PayloadAction<string>) => {
            state.sidebarTitle = action.payload;
        },
        setVehicleRoute: (state, action: PayloadAction<VehicleTripSegment[]>) => {
            state.vehicleRoute = action.payload;
        },
        setTripStartDate(state, action: PayloadAction<Date | null>) {
            state.tripStartDate = action.payload;
        },
        setTripEndDate(state, action: PayloadAction<Date | null>) {
            state.tripEndDate = action.payload;
        },
        resetTripDateRangeToToday(state) {
            state.tripStartDate = dayjs().startOf("day").toDate();
            state.tripEndDate = dayjs().startOf("day").add(1, "day").toDate();
        },
        setVehicles(state, action: PayloadAction<VehicleListItemDto[]>) {
            state.vehicles = action.payload;
        },
        setPositions(state, action: PayloadAction<VehiclePositionInfo[]>) {
            state.positions = action.payload;
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVehicleTripSegments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchVehicleTripSegments.fulfilled, (state, action) => {
                state.loading = false;
                state.vehicleRoute = action.payload;
                state.mapData = transformVehicleTripSegments(action.payload);
            })
            .addCase(fetchVehicleTripSegments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(fetchGeocodedTripStopPositionAddresses.pending, (state) => {
                state.segmentStoppedAddressesLoading = true;
            })
            .addCase(fetchGeocodedTripStopPositionAddresses.fulfilled, (state, action) => {
                const streetAddresses = action.payload;

                // Convert to easy lookup: { id → data }
                const byId = Object.fromEntries(
                    streetAddresses.map(g => [g.id, g])
                );

                // Merge geocoded info into the route
                state.vehicleRoute = state.vehicleRoute.map(seg => {
                    const geocoded = byId[seg.startTime];
                    if (geocoded) {
                        return {
                            ...seg,
                            streetAddress: geocoded.streetAddress
                        };
                    }
                    return seg;
                });
                state.segmentStoppedAddressesLoading = false;
                state.mapData = transformVehicleTripSegments(state.vehicleRoute);
            })
            .addCase(fetchGeocodedTripStopPositionAddresses.rejected, (state) => {
                state.segmentStoppedAddressesLoading = false;
            })
            .addCase(fetchGeocodedVehicleAddress.pending, (state) => {
                state.segmentStoppedAddressesLoading = true;
            })
            .addCase(fetchGeocodedVehicleAddress.fulfilled, (state, action) => {
                const geocodeData = action.payload;
                state.vehicleAddressLoading = false;
                state.vehicleStreetAddress = geocodeData?.streetAddress;
                if (geocodeData.error) {
                    console.error(geocodeData.error);
                }
            })
            .addCase(fetchGeocodedVehicleAddress.rejected, (state) => {
                state.vehicleAddressLoading = false;
            });
    },
});

export const {
    setGroupMapPoints,
    setActiveTab,
    setIsWorkShiftView,
    toggleWorkShiftView,
    setIsVehicleDetailView,
    toggleVehicleDetailView,
    setSelectedVehicleId,
    setSelectedEmployeeId,
    setSelectedWorkShiftTimeSlotId,
    setSelectedWorkShift,
    setSelectedRouteId,
    setHoveredRouteId,
    setSidebarTitle,
    setVehicleRoute,
    setTripStartDate,
    setTripEndDate,
    setVehicles,
    setPositions,
    setLoading,
    setIsVehicleRouteView,
    resetTripDateRangeToToday
} = monitorViewSlice.actions;
export const monitoriViewReducer = monitorViewSlice.reducer;